import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Camera } from "lucide-react";

/**
 * Rotating photo carousel with:
 *   - Auto-advance every 6s, pauses on hover
 *   - Prev/Next arrows, clickable dot indicators
 *   - Touch swipe support
 *   - Keyboard arrow key nav (when carousel has focus / visible)
 *
 * Add more slides by appending to the `SLIDES` array below.
 * Shape: { image: string, caption: string, alt: string }
 */

const DINER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_bold-davinci-9/artifacts/8wr4w9id_unnamed-1.jpg";
const BASEBALL_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_bold-davinci-9/artifacts/qwv9dk5b_unnamed.jpg";

const SLIDES = [
  {
    image: BASEBALL_IMG,
    alt:
      "Greg Clark with Sigma Tau Chi alumni at a Fort Ontario fundraiser benefiting Oswego Little League.",
    caption:
      "The Sigma Tau Chi Alumni Association and active fraternity brothers held a fundraiser at Fort Ontario in early June for the benefit of Oswego Little League. I\u2019m fourth from the left.",
  },
  {
    image: DINER_IMG,
    alt:
      "Greg Clark shaking hands with Oswego County Sheriff Don Hilton outside Wade's Diner.",
    caption:
      "I recently met with Sheriff Don Hilton to discuss public safety issues facing Oswego County.",
  },
];

const AUTO_ADVANCE_MS = 6000;

const PhotoCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = SLIDES.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const goTo = useCallback((i) => setIndex(((i % total) + total) % total), [total]);

  // Auto-advance
  useEffect(() => {
    if (paused || total <= 1) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, total]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Touch swipe
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const current = SLIDES[index];

  return (
    <section
      className="py-20 lg:py-24 bg-white"
      aria-roledescription="carousel"
      aria-label="Campaign photos"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#b22234] font-bold mb-4 inline-flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Out on the Campaign Trail
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-extrabold text-[#0a3161]">
            Photos
          </h2>
          <div className="mt-6 h-1 w-20 bg-[#b22234] rounded-full mx-auto" />
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Slide viewport */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-100 aspect-[16/10]">
            {SLIDES.map((slide, i) => (
              <img
                key={i}
                src={slide.image}
                alt={slide.alt}
                aria-hidden={i !== index}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
                  i === index ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}

            {/* Arrows */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous slide"
              className="absolute top-1/2 left-3 md:left-5 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-[#0a3161] shadow-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-4 focus:ring-white/60"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next slide"
              className="absolute top-1/2 right-3 md:right-5 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-[#0a3161] shadow-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-4 focus:ring-white/60"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Slide counter badge */}
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/50 text-white text-xs font-oswald font-semibold tracking-[0.14em] uppercase backdrop-blur">
              {index + 1} / {total}
            </div>
          </div>

          {/* Caption */}
          <p
            key={index /* re-trigger fade */}
            className="mt-6 text-center text-[17px] md:text-[19px] leading-[1.7] text-[#1f2a44] max-w-3xl mx-auto fade-up"
          >
            {current.caption}
          </p>

          {/* Dot indicators */}
          <div className="mt-6 flex items-center justify-center gap-3">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                className={`h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#0a3161]/50 ${
                  i === index
                    ? "w-8 bg-[#b22234]"
                    : "w-2.5 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhotoCarousel;
